import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Session } from '@supabase/supabase-js'

import { supabase, type Row } from '@/lib/supabase'

export type StaffRecord = Row<'staff'>

/**
 * The Supabase session, kept in React state and in sync with the client.
 *
 * `loading` is true only until the first `onAuthStateChange` fires, which is
 * what stops the sign-in page flashing on every reload while the SDK reads the
 * persisted session out of storage.
 */
export function useSession(): { session: Session | null; loading: boolean } {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    let active = true
    // Whose data is in the cache. The SDK fires SIGNED_IN again every time the
    // tab becomes visible, for the same person; clearing the cache then threw
    // the whole app back to "Opening the studio" and lost whatever she was
    // typing (28 Oct 2026). Only a different person, or nobody, clears it.
    let userId: string | null | undefined

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      userId ??= data.session?.user.id ?? null
      setSession(data.session)
      setLoading(false)
    })

    const { data: subscription } = supabase.auth.onAuthStateChange((event, next) => {
      if (!active) return

      const nextId = next?.user.id ?? null
      setSession(next)
      setLoading(false)

      // A different person signing in must not see the previous person's cached
      // rows, and a sign-out must not leave data sitting in memory.
      if (event === 'SIGNED_OUT' || (event === 'SIGNED_IN' && userId !== undefined && userId !== nextId)) {
        queryClient.clear()
      }
      userId = nextId
    })

    return () => {
      active = false
      subscription.subscription.unsubscribe()
    }
  }, [queryClient])

  return { session, loading }
}

/**
 * The caller's staff record, or null if they have one but it is not readable —
 * which, given the policy on `staff`, means they are not staff at all.
 *
 * This is the client-side half of `is_staff()`. It is a convenience for the UI,
 * never a security boundary: the database decides, and a client that lies about
 * this still reads nothing.
 */
export function useStaff(session: Session | null) {
  return useQuery({
    queryKey: ['staff', session?.user.id],
    enabled: Boolean(session?.user.id),
    // Role and MFA state change so rarely that refetching them on every window
    // focus is pure noise.
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<StaffRecord | null> => {
      const { data, error } = await supabase
        .from('staff')
        .select('*')
        .eq('auth_user_id', session?.user.id ?? '')
        .maybeSingle()

      if (error) throw error
      return data
    },
  })
}

/**
 * The assurance level of the current session. 'aal2' means a second factor was
 * verified. Staff rows with mfa_required read nothing until this is aal2, so the
 * UI needs to know in order to send them to the challenge instead of a wall of
 * empty tables.
 */
export function useAssuranceLevel(session: Session | null) {
  return useQuery({
    queryKey: ['aal', session?.user.id],
    enabled: Boolean(session?.user.id),
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (error) throw error
      return data
    },
  })
}
